import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, MapPin, Navigation, Sprout, Layers, Dumbbell, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import SectionHeading from "./SectionHeading";
import { GALERIA, ESTRUTURA, UNIDADES } from "../data/siteData";

const ICONES = { Sprout, Layers, Dumbbell, Users };

const Lightbox = ({ index, onClose, onNav }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  const foto = GALERIA[index];
  return (
    <motion.div
      data-testid="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-night/95 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-button"
        onClick={onClose}
        aria-label="Fechar foto ampliada"
        className="absolute right-4 top-4 rounded-full border border-white/20 bg-navy p-2.5 text-white transition-colors hover:text-gold"
      >
        <X size={22} />
      </button>
      <button
        data-testid="lightbox-prev-button"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label="Foto anterior"
        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-navy p-2.5 text-white transition-colors hover:text-gold sm:left-6"
      >
        <ChevronLeft size={24} />
      </button>
      <motion.img
        key={index}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        src={foto.src}
        alt={foto.alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] max-w-full rounded-2xl object-contain shadow-2xl"
      />
      <button
        data-testid="lightbox-next-button"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label="Próxima foto"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-navy p-2.5 text-white transition-colors hover:text-gold sm:right-6"
      >
        <ChevronRight size={24} />
      </button>
      <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.2em] text-white/60">
        {index + 1} / {GALERIA.length} — {foto.alt}
      </p>
    </motion.div>
  );
};

const Espaco = () => {
  const [aberta, setAberta] = useState(null);
  const nav = (dir) => setAberta((i) => (i + dir + GALERIA.length) % GALERIA.length);

  return (
    <section id="espaco" data-testid="espaco-section" className="relative bg-night py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Estrutura completa"
          title="Nosso Espaço"
          lead="Campos de grama natural e sintético, iluminação de estádio e área de convivência para a família."
        />

        <div className="columns-2 gap-4 md:columns-3 [&>figure]:mb-4">
          {GALERIA.map((foto, i) => (
            <Reveal key={i} delay={(i % 3) * 0.07} className="break-inside-avoid">
              <figure
                data-testid={`gallery-item-${i}`}
                onClick={() => setAberta(i)}
                className="group relative cursor-zoom-in overflow-hidden rounded-2xl"
              >
                <img
                  src={foto.src}
                  alt={foto.alt}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </figure>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ESTRUTURA.map((e, i) => {
            const Icon = ICONES[e.icone];
            return (
              <Reveal key={e.nome} delay={i * 0.07}>
                <div
                  data-testid={`estrutura-card-${i}`}
                  className="h-full rounded-2xl border border-white/10 bg-navy/60 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-lawn/50"
                >
                  <Icon size={28} strokeWidth={1.8} className="mb-3 text-lawn" />
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-white">{e.nome}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/65">{e.descricao}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-24">
          <SectionHeading
            eyebrow="Onde estamos"
            title={UNIDADES.length > 1 ? "Nossas unidades" : "Nossa unidade"}
          />
          <div className={UNIDADES.length > 1 ? "grid gap-8 lg:grid-cols-2" : "mx-auto max-w-3xl"}>
            {UNIDADES.map((u, i) => (
              <Reveal key={u.nome} delay={i * 0.1}>
                <article
                  data-testid={`unidade-card-${i}`}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-navy/60"
                >
                  <div className="p-6 md:p-8">
                    <h3 className="font-display text-3xl uppercase tracking-wide text-white">{u.nome}</h3>
                    <p className="mt-3 flex items-start gap-2 text-sm text-white/75">
                      <MapPin size={17} className="mt-0.5 shrink-0 text-gold" />
                      {u.endereco}
                    </p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(u.mapsQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`unidade-directions-${i}`}
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-lawn px-6 py-2.5 text-xs font-bold uppercase tracking-wide text-navy transition-transform duration-200 hover:scale-105"
                    >
                      <Navigation size={14} /> Como chegar
                    </a>
                  </div>
                  <iframe
                    title={`Mapa — ${u.nome}`}
                    data-testid={`unidade-map-${i}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(u.mapsQuery)}&output=embed`}
                    loading="lazy"
                    className="h-64 w-full border-0"
                    allowFullScreen
                  />
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {aberta !== null && (
          <Lightbox index={aberta} onClose={() => setAberta(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Espaco;
